import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router';

import { ErrorState } from './ErrorState';

const referenceFor = (error: Error) => {
  const seed = `${error.name}:${error.message}:${error.stack?.slice(0, 200) ?? ''}`;
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (Math.imul(hash, 31) + seed.charCodeAt(index)) | 0;
  }
  return Math.abs(hash).toString(36).toUpperCase().padStart(6, '0').slice(0, 6);
};

interface InnerProps {
  children: ReactNode;
  resetKey: number;
  onRecovered: () => void;
}

interface InnerState {
  error: Error | null;
  reference: string | null;
}

class Boundary extends Component<InnerProps, InnerState> {
  state: InnerState = { error: null, reference: null };

  static getDerivedStateFromError(error: Error): InnerState {
    return { error, reference: referenceFor(error) };
  }

  componentDidUpdate(previous: InnerProps) {
    if (previous.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null, reference: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`Uncaught error [${referenceFor(error)}]:`, error, info);
  }

  render() {
    const { error, reference } = this.state;
    if (!error) return this.props.children;

    return (
      <ErrorState
        error={error}
        reference={reference ?? undefined}
        onRetry={this.props.onRecovered}
      />
    );
  }
}

export function ErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    setResetKey((key) => key + 1);
  }, [location.key]);

  return (
    <Boundary resetKey={resetKey} onRecovered={() => setResetKey((key) => key + 1)}>
      {children}
    </Boundary>
  );
}
